
import { useState, useEffect } from 'react';
import { format, subDays } from 'date-fns';
import { getAnalyticsData } from '@/services/analyticsService';
import { AnalyticsData, PerformanceMetricRecord } from '@/services/analytics/types';

export function useAnalyticsData(timeRange: string) {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      
      const endDate = new Date();
      const startDate = subDays(endDate, parseInt(timeRange));
      
      try {
        const data = await getAnalyticsData(startDate, endDate);
        setAnalyticsData(data);
      } catch (error) {
        console.error('Error fetching analytics data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [timeRange]);

  // Process game sessions by day
  const getGameSessionsByDay = () => {
    if (!analyticsData?.gameSessions) return [];
    
    // Create a map of days to session counts
    const dayMap = new Map();
    const days = parseInt(timeRange);
    
    // Initialize all days with 0 count
    for (let i = days; i >= 0; i--) {
      const date = subDays(new Date(), i);
      const dateStr = format(date, 'yyyy-MM-dd');
      dayMap.set(dateStr, { day: dateStr, count: 0, displayDay: format(date, 'EEE') });
    }
    
    // Count sessions by day
    analyticsData.gameSessions.forEach((session) => {
      const dateStr = session.started_at.split('T')[0];
      if (dayMap.has(dateStr)) {
        const dayData = dayMap.get(dateStr);
        dayData.count += 1;
        dayMap.set(dateStr, dayData);
      }
    });
    
    return Array.from(dayMap.values());
  };
  
  // Process SOS events by day
  const getSosEventsByDay = () => {
    if (!analyticsData?.sosEvents) return [];
    
    const dayMap = new Map();
    const days = parseInt(timeRange);
    
    // Initialize all days with 0 count
    for (let i = days; i >= 0; i--) {
      const date = subDays(new Date(), i);
      const dateStr = format(date, 'yyyy-MM-dd');
      dayMap.set(dateStr, { day: dateStr, count: 0, displayDay: format(date, 'EEE') });
    }
    
    // Count SOS events by day
    analyticsData.sosEvents.forEach((event) => {
      const dateStr = event.created_at.split('T')[0];
      if (dayMap.has(dateStr)) {
        const dayData = dayMap.get(dateStr);
        dayData.count += 1;
        dayMap.set(dateStr, dayData);
      }
    });
    
    return Array.from(dayMap.values());
  };
  
  // Process performance metrics
  const getPerformanceMetrics = () => {
    if (!analyticsData?.performanceMetrics) return new Map<string, any[]>();
    
    // Group by metric name
    const metricsByName = new Map<string, any[]>();
    
    analyticsData.performanceMetrics.forEach((metric: PerformanceMetricRecord) => {
      const name = metric.event_data.name;
      if (!metricsByName.has(name)) {
        metricsByName.set(name, []);
      }
      
      metricsByName.get(name)?.push({
        timestamp: metric.timestamp,
        value: metric.event_data.value,
        game_id: metric.event_data.game_id,
        displayTime: format(new Date(metric.timestamp), 'HH:mm')
      });
    });
    
    // Sort metrics by time
    metricsByName.forEach((metrics, name) => {
      metrics.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    });
    
    return metricsByName;
  };

  return {
    loading,
    analyticsData,
    getGameSessionsByDay,
    getSosEventsByDay,
    getPerformanceMetrics,
    hasData: !!analyticsData
  };
}
